import type { Vector3 } from "three";

/**
 * A single resolved surface drag area (one panel of a box side).
 */
export interface ResolvedSurfaceArea {
  bounds: { min: Vector3; max: Vector3 };
  /** In-plane center of this panel, for per-area center snapping. */
  center?: Vector3;
}

/**
 * Constrains a point to a surface's movement area.
 *
 * The incoming `point` already lies on the drag plane that was built through the
 * part's real position (with the surface normal) at pointer-down, so its
 * surface-normal coordinate is the authoritative one the build uses. We must NOT
 * re-project it onto a bounds-derived plane or clamp the normal axis: the
 * constraint bounds come from the panel's inner face (`innerCenter[normal]`)
 * while the build places the part on the outer face (`outerCenter[normal]`), and
 * forcing the normal coordinate would shove the part through the panel by its
 * thickness (jump on drag start, jump back on reconcile). Instead we keep the
 * point's normal coordinate and clamp only the two in-plane axes to the bounds.
 */
export function projectOntoSurface(
  point: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  const clampAxis = (value: number, min: number, max: number) =>
    Math.max(Math.min(min, max), Math.min(Math.max(min, max), value));

  const projected = point.clone();
  // Only clamp axes that lie in the surface plane (normal component ~0); leave
  // the surface-normal axis at the point's own (correct) value.
  if (Math.abs(planeNormal.x) < 0.9) {
    projected.x = clampAxis(projected.x, bounds.min.x, bounds.max.x);
  }
  if (Math.abs(planeNormal.y) < 0.9) {
    projected.y = clampAxis(projected.y, bounds.min.y, bounds.max.y);
  }
  if (Math.abs(planeNormal.z) < 0.9) {
    projected.z = clampAxis(projected.z, bounds.min.z, bounds.max.z);
  }

  return projected;
}

/**
 * True when `point`'s in-plane coordinates lie inside `bounds`. The
 * surface-normal axis is ignored (same rule as {@link projectOntoSurface}).
 */
export function isPointInsideSurfaceBounds(
  point: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): boolean {
  if (Math.abs(planeNormal.x) < 0.9) {
    if (point.x < bounds.min.x || point.x > bounds.max.x) {
      return false;
    }
  }
  if (Math.abs(planeNormal.y) < 0.9) {
    if (point.y < bounds.min.y || point.y > bounds.max.y) {
      return false;
    }
  }
  if (Math.abs(planeNormal.z) < 0.9) {
    if (point.z < bounds.min.z || point.z > bounds.max.z) {
      return false;
    }
  }
  return true;
}

/**
 * Whether snapping `point` along `axis` onto `center` would keep it inside the
 * active panel bounds (multi-area surfaces with global center snapping).
 */
export function canAxisCenterSnapStayInBounds(
  point: Vector3,
  axis: Vector3,
  center: Vector3,
  planeNormal: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): boolean {
  const distToCenter = point.clone().sub(center).dot(axis);
  const trial = point.clone().addScaledVector(axis, -distToCenter);
  return isPointInsideSurfaceBounds(trial, planeNormal, bounds);
}

/**
 * Projects a point onto the nearest of several surface areas (panels). Each
 * area is clamped independently (in-plane only, normal coord preserved); the
 * area whose clamp is closest to the raw point wins. This keeps the part on a
 * valid panel and makes it "jump" across the gap to the next panel once the
 * pointer passes the midpoint of the gap.
 */
export function projectOntoSurfaceAreas(
  point: Vector3,
  planeNormal: Vector3,
  areas: ResolvedSurfaceArea[],
): { position: Vector3; index: number } {
  let bestPos: Vector3 | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  let bestIdx = -1;
  areas.forEach((area, i) => {
    const projected = projectOntoSurface(point, planeNormal, area.bounds);
    const dist = point.distanceTo(projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestPos = projected;
      bestIdx = i;
    }
  });
  return { position: bestPos ?? point.clone(), index: bestIdx };
}

/**
 * Builds the line-segment vertex list (pairs of points) outlining a rectangle
 * on an axis-aligned surface plane, keeping the surface-normal coordinate fixed
 * at the box center on the normal axis.
 */
export function surfaceRectSegments(
  min: Vector3,
  max: Vector3,
  planeNormal: Vector3,
): number[] {
  if (Math.abs(planeNormal.x) > 0.9) {
    const x = (min.x + max.x) / 2;
    return [
      x,
      min.y,
      min.z,
      x,
      max.y,
      min.z,
      x,
      max.y,
      min.z,
      x,
      max.y,
      max.z,
      x,
      max.y,
      max.z,
      x,
      min.y,
      max.z,
      x,
      min.y,
      max.z,
      x,
      min.y,
      min.z,
    ];
  }
  if (Math.abs(planeNormal.y) > 0.9) {
    const y = (min.y + max.y) / 2;
    return [
      min.x,
      y,
      min.z,
      max.x,
      y,
      min.z,
      max.x,
      y,
      min.z,
      max.x,
      y,
      max.z,
      max.x,
      y,
      max.z,
      min.x,
      y,
      max.z,
      min.x,
      y,
      max.z,
      min.x,
      y,
      min.z,
    ];
  }
  const z = (min.z + max.z) / 2;
  return [
    min.x,
    min.y,
    z,
    max.x,
    min.y,
    z,
    max.x,
    min.y,
    z,
    max.x,
    max.y,
    z,
    max.x,
    max.y,
    z,
    min.x,
    max.y,
    z,
    min.x,
    max.y,
    z,
    min.x,
    min.y,
    z,
  ];
}

/** Default in-drag grid step (mm) while Ctrl/Cmd is held without Shift. */
export const DEFAULT_DRAG_GRID_STEP_MM = 10;

/** Fine in-drag grid step (mm) while Ctrl/Cmd+Shift is held. */
export const FINE_DRAG_GRID_STEP_MM = 1;

/**
 * Rounds a position along each draggable axis to the nearest grid step. Axes
 * must be unit vectors for independent degrees of freedom (orthogonal in-plane
 * axes for surfaces, edge direction for 1D drags). Components not aligned with
 * any axis are unchanged.
 */
export function snapPositionToDragGrid(
  position: Vector3,
  axes: Vector3[],
  stepMm: number,
): Vector3 {
  if (stepMm <= 0 || axes.length === 0) {
    return position.clone();
  }
  const snapped = position.clone();
  for (const axis of axes) {
    const coord = snapped.dot(axis);
    const rounded = Math.round(coord / stepMm) * stepMm;
    snapped.addScaledVector(axis, rounded - coord);
  }
  return snapped;
}

/**
 * Grid step from pointer modifiers: Shift = 1 mm, otherwise 10 mm. Shift is one
 * "fine and free" mode, so it also switches off snapping — see
 * {@link dragSnapThresholdFromModifiers}, the other half of this rule. Keep the
 * two together so they cannot drift apart.
 */
export function dragGridStepFromModifiers(shiftKey: boolean): number {
  return shiftKey ? FINE_DRAG_GRID_STEP_MM : DEFAULT_DRAG_GRID_STEP_MM;
}

/** Snap radius (mm) for center lines, diagonals and discrete anchors. */
export const DEFAULT_DRAG_SNAP_THRESHOLD_MM = 15;

/**
 * Snap radius while Shift is held: none. Every snap test in `DraggablePart`
 * compares strictly (`distance < threshold`), so zero degenerates to "nothing
 * ever snaps" - including a point sitting exactly on a center line - while the
 * movement bounds clamp and the guide lines keep working unchanged.
 */
export const FREE_DRAG_SNAP_THRESHOLD_MM = 0;

/**
 * Snap radius from pointer modifiers. A 15 mm magnet makes a deliberate small
 * offset (a handle 3 mm off the panel center) unreachable, so Shift turns every
 * snap off for the duration of the gesture: an escape hatch per drag, not a
 * setting. Pairs with {@link dragGridStepFromModifiers}.
 */
export function dragSnapThresholdFromModifiers(shiftKey: boolean): number {
  return shiftKey
    ? FREE_DRAG_SNAP_THRESHOLD_MM
    : DEFAULT_DRAG_SNAP_THRESHOLD_MM;
}

/**
 * How long an end cap is relative to the travel range it bounds, and the
 * millimetre bounds that ratio is held between. Proportional rather than fixed
 * so a cap reads the same against a 60 mm range and a 600 mm one; clamped so it
 * neither disappears on a long rail nor swallows a short one.
 */
const EDGE_CAP_RATIO = 0.045;
const EDGE_CAP_MIN_MM = 12;
const EDGE_CAP_MAX_MM = 40;

/**
 * Builds the line-segment vertex list for an edge (single-axis) movement range:
 * the travel line itself, plus one short upright bar across each end.
 *
 * The bare line was read as a guide rather than as a limit — nothing about it
 * said where the part runs out of travel. The end caps are the dimension-line
 * convention for exactly that, and they turn the line into a measured span with
 * two visible stops.
 *
 * The counterpart to {@link surfaceRectSegments}, which needs no such treatment
 * because a closed rectangle already reads as a boundary.
 */
export function edgeRangeSegments(min: Vector3, max: Vector3): number[] {
  const dx = max.x - min.x;
  const dy = max.y - min.y;
  const dz = max.z - min.z;
  const length = Math.hypot(dx, dy, dz);

  // A range with no travel has no ends to mark.
  if (length < 1e-6) return [];

  const ux = dx / length;
  const uy = dy / length;
  const uz = dz / length;

  // The cap stands upright, which is how a stop is drawn on a plan and what
  // keeps it square across the line from any orbit around a horizontal range.
  // World up collapses when the range itself is vertical, so fall back to a
  // horizontal reference there.
  const vertical = Math.abs(uy) > 0.9;
  const rx = 0;
  const ry = vertical ? 0 : 1;
  const rz = vertical ? 1 : 0;

  // Cap direction: the reference with its along-axis part removed.
  const dot = rx * ux + ry * uy + rz * uz;
  let ax = rx - ux * dot;
  let ay = ry - uy * dot;
  let az = rz - uz * dot;
  const aLen = Math.hypot(ax, ay, az);
  if (aLen < 1e-6) return [];
  ax /= aLen;
  ay /= aLen;
  az /= aLen;

  const cap = Math.min(
    Math.max(length * EDGE_CAP_RATIO, EDGE_CAP_MIN_MM),
    EDGE_CAP_MAX_MM,
  );

  const segments = [min.x, min.y, min.z, max.x, max.y, max.z];

  for (const end of [min, max]) {
    segments.push(
      end.x - ax * cap,
      end.y - ay * cap,
      end.z - az * cap,
      end.x + ax * cap,
      end.y + ay * cap,
      end.z + az * cap,
    );
  }

  return segments;
}

/** The world axes lying in the plane (the normal is axis-aligned). */
function inPlaneComponents(planeNormal: Vector3): Array<"x" | "y" | "z"> {
  return (["x", "y", "z"] as const).filter(
    (axis) => Math.abs(planeNormal[axis]) < 0.9,
  );
}

/**
 * True when the area has no room on some in-plane axis: its padded range came
 * out inverted (max before min), which means the part does not fit on that
 * panel. The buildstep no longer emits such areas; this guards against any
 * other producer so an inverted box is never drawn or clamped into.
 */
export function isSurfaceAreaEmpty(
  area: Pick<ResolvedSurfaceArea, "bounds">,
  planeNormal: Vector3,
): boolean {
  return inPlaneComponents(planeNormal).some(
    (axis) => area.bounds.max[axis] < area.bounds.min[axis],
  );
}

/** In-plane center of an area: its own center, else the bounds midpoint. */
export function surfaceAreaCenter(area: ResolvedSurfaceArea): Vector3 {
  return (
    area.center?.clone() ??
    area.bounds.min.clone().add(area.bounds.max).multiplyScalar(0.5)
  );
}

/**
 * The center a part on `area` snaps to, one coordinate per in-plane axis.
 *
 * With a `globalCenter` (the side's single "Center" anchor, "Relativ zu Case")
 * each axis takes the global coordinate where it lies inside the area's range
 * on that axis, and the area's own center coordinate where it does not: on a
 * side split by the lid cut the case center can fall in the gap between the
 * panels, and a snap line there advertises a position the part can never take.
 * Without a global center (per-area mode, "Relativ zu Korpus") the result is
 * simply the area center. The surface-normal coordinate always comes from the
 * area, so the guide stays on the face.
 */
export function resolveAreaSnapCenter(
  area: ResolvedSurfaceArea,
  planeNormal: Vector3,
  globalCenter: Vector3 | null,
): Vector3 {
  const center = surfaceAreaCenter(area);
  if (!globalCenter) return center;
  for (const axis of inPlaneComponents(planeNormal)) {
    const lo = Math.min(area.bounds.min[axis], area.bounds.max[axis]);
    const hi = Math.max(area.bounds.min[axis], area.bounds.max[axis]);
    const value = globalCenter[axis];
    if (value >= lo && value <= hi) {
      center[axis] = value;
    }
  }
  return center;
}

/** A center guide line of one area along one in-plane axis. */
export interface AreaGuideSegment {
  dir: Vector3;
  start: Vector3;
  end: Vector3;
}

/**
 * One center guide line per in-plane axis, each spanning the area's full range
 * along that axis and passing through `snapCenter`. The endpoints are explicit
 * rather than `center ± half extent`, so a snap center that is off the area's
 * middle (a reachable global coordinate near one edge) still yields a line that
 * ends at the area's edges instead of overshooting one and stopping short of
 * the other.
 *
 * Returns `null` in place of an axis with no extent (a collapsed axis on a
 * panel the part only fits on along the other one) so the index of each entry
 * still matches `axes`, which the snap-state lookup relies on.
 */
export function areaCenterGuideSegments(
  area: ResolvedSurfaceArea,
  snapCenter: Vector3,
  axes: Vector3[],
): Array<AreaGuideSegment | null> {
  return axes.map((dir) => {
    const a = area.bounds.min.dot(dir);
    const b = area.bounds.max.dot(dir);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi - lo < 1e-6) return null;
    const along = snapCenter.dot(dir);
    return {
      dir,
      start: snapCenter.clone().addScaledVector(dir, lo - along),
      end: snapCenter.clone().addScaledVector(dir, hi - along),
    };
  });
}

/**
 * How parallel two unit vectors have to be to count as the same axis.
 *
 * The same 0.9 used everywhere else in this file to decide whether a world axis
 * lies in a plane: the axes involved are either identical or perpendicular, so
 * anything between the two is a rounding artefact rather than a real angle.
 */
export const AXIS_PARALLEL_DOT = 0.9;

/** Whether `axis` runs along `reference` (either way round). */
export function isAxisParallelTo(axis: Vector3, reference: Vector3): boolean {
  return Math.abs(axis.dot(reference)) > AXIS_PARALLEL_DOT;
}

/**
 * Constrains a drag point to a single axis through `origin` — the gizmo rule
 * behind grabbing a direction arrow: the part slides along the arrow and its
 * perpendicular coordinates stay exactly where the press found them.
 *
 * Applied *before* the constraint clamp, so the surface projection still sees a
 * point with the authoritative surface-normal coordinate (see
 * `projectOntoSurface` above): the locked line runs through the part's own
 * position, so anything off it — including the off-plane component the
 * camera-facing drag plane introduces — is discarded here.
 */
export function projectOntoLockedAxis(
  point: Vector3,
  origin: Vector3,
  axis: Vector3,
): Vector3 {
  const along = point.clone().sub(origin).dot(axis);
  return origin.clone().addScaledVector(axis, along);
}

export interface ConstrainedDragProjection {
  constrainedPosition: Vector3;
  /** The center the part snaps to on the panel it is over. */
  activeCenter: Vector3;
  /** Index into the areas of the panel the part is over; null without areas. */
  activeAreaIndex: number | null;
}

/**
 * Clamps a drag point to the constraint. Multi-area surfaces clamp to the
 * nearest panel (the part jumps across the gap) and report that panel's snap
 * center; everything else goes through `projectPoint` and keeps the default
 * center (the first area's snap center for a single-bounds surface, the
 * constraint center for an edge).
 */
export function projectConstrainedDragPosition(
  newLocalPosition: Vector3,
  args: {
    constraintType: "edge" | "surface";
    planeNormal: Vector3 | undefined;
    surfaceAreas: ResolvedSurfaceArea[];
    /** Snap center per area, same order as `surfaceAreas` (or `[single]`). */
    areaSnapCenters: Vector3[];
    defaultCenter: Vector3;
    projectPoint: (point: Vector3) => Vector3;
  },
): ConstrainedDragProjection {
  const {
    constraintType,
    planeNormal,
    surfaceAreas,
    areaSnapCenters,
    defaultCenter,
    projectPoint,
  } = args;

  if (constraintType === "surface" && planeNormal && surfaceAreas.length > 0) {
    const projection = projectOntoSurfaceAreas(
      newLocalPosition,
      planeNormal,
      surfaceAreas,
    );
    return {
      constrainedPosition: projection.position,
      activeCenter: areaSnapCenters[projection.index] ?? defaultCenter,
      activeAreaIndex: projection.index,
    };
  }

  return {
    constrainedPosition: projectPoint(newLocalPosition),
    activeCenter:
      constraintType === "surface"
        ? (areaSnapCenters[0] ?? defaultCenter)
        : defaultCenter,
    activeAreaIndex: null,
  };
}
